import { Chart } from "primereact/chart";
import { Button } from "primereact/button";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Calendar } from "primereact/calendar";
import { Form, useLoaderData } from "react-router-dom";
import { CurrentMonthCharges, StatsDashboard, StatsDeliveries } from "../../../data/loca/rentals";
import { getSatisfactionDashboard } from "../../../data/loca/satisfaction";
import { CommentsList, Distribution, KpiGrid, Stars, formatRate, formatScore } from "../satisfaction/SatisfactionStats";
import DeliveryStats from "./DeliveryStats";
import "../../../assets/css/satisfaction.css";

export async function loader({ params }) {
    const [dashboard, satisfaction, deliveries] = await Promise.all([
        StatsDashboard(params.start, params.end),
        getSatisfactionDashboard(params.start, params.end).catch(() => null),
        StatsDeliveries(params.start, params.end).catch(() => null)
    ]);
    const start = params.start;
    const end = params.end;
    return { dashboard, satisfaction, deliveries, start, end };
}

export async function action({ request }) {
    const formData = await request.formData();
    const data = Object.fromEntries(formData);
    const splittedDates = data.dates.split(" - ");
    const st = moment(splittedDates[0], ["DD/MM/YYYY", "DD-MM-YYYY"]).format("YYYY-MM-DD");
    const en = moment(splittedDates[1], ["DD/MM/YYYY", "DD-MM-YYYY"]).format("YYYY-MM-DD");

    const redirectUrl = `/stats/${st}/${en}`;
    // redirection JS pour forcer le reload de la page
    return (window.location.href = redirectUrl);
}

const formatCurrency = (value) =>
    value.toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    });

const formatNumber = (value) => value.toLocaleString("fr-FR");

const formatPercent = (value) =>
    `${value.toLocaleString("fr-FR", { maximumFractionDigits: 1 })}%`;

const clampRatio = (value) => {
    if (!Number.isFinite(value)) {
        return 0;
    }
    return Math.max(0, Math.min(100, value));
};

const RENTALS_COLORS = [
    "rgba(158, 235, 129, 0.6)",
    "rgba(235, 163, 129, 0.6)",
    "rgba(129, 133, 235, 0.6)",
    "rgba(51, 34, 26, 0.5)",
    "rgba(107, 89, 80, 0.5)",
    "rgba(80, 51, 26, 0.5)",
    "rgba(53, 54, 82, 0.5)",
    "rgba(88, 107, 80, 0.5)",
    "rgba(113, 113, 133, 0.5)",
    "rgba(138, 139, 184, 0.5)",
    "rgba(146, 171, 137, 0.5)",
    "rgba(171, 148, 137, 0.5)",
];

const SERVICES_COLORS = [
    "rgba(158, 235, 129, 0.75)",
    "rgba(235, 163, 129, 0.75)",
    "rgba(129, 133, 235, 0.75)",
    "rgba(51, 34, 26, 0.6)",
    "rgba(53, 54, 82, 0.6)",
    "rgba(88, 107, 80, 0.6)",
];

const TOP_TOOLS_BACKGROUND_COLORS = [
    "rgba(79, 124, 255, 0.35)",
    "rgba(123, 91, 255, 0.35)",
    "rgba(52, 199, 171, 0.35)",
    "rgba(255, 159, 64, 0.35)",
    "rgba(146, 171, 137, 0.45)",
];

const TOP_TOOLS_BORDER_COLORS = [
    "rgba(79, 124, 255, 0.75)",
    "rgba(123, 91, 255, 0.75)",
    "rgba(52, 199, 171, 0.75)",
    "rgba(255, 159, 64, 0.75)",
    "rgba(146, 171, 137, 0.75)",
];

function Stats() {
    const { dashboard, satisfaction, deliveries, start, end } = useLoaderData();
    const currentMonthStart = new Date();
    currentMonthStart.setDate(1);
    currentMonthStart.setHours(0, 0, 0, 0);
    const isAfterCurrentMonth = (monthValue) => {
        const monthDate = new Date(monthValue);
        if (Number.isNaN(monthDate.getTime())) {
            return false;
        }

        const monthKey = monthDate.getFullYear() * 12 + monthDate.getMonth();
        const currentMonthKey = currentMonthStart.getFullYear() * 12 + currentMonthStart.getMonth();
        return monthKey > currentMonthKey;
    };

    const startDate = new Date(start);
    const endDate = new Date(end);
    const [dates, setDates] = useState([startDate, endDate]);
    const [chargesExpanded, setChargesExpanded] = useState(false);
    const [chargeGroups, setChargeGroups] = useState([]);
    const [chargesLoaded, setChargesLoaded] = useState(false);
    const [chargesLoading, setChargesLoading] = useState(false);
    const [chargesError, setChargesError] = useState(null);
    const [expandedChargeGroups, setExpandedChargeGroups] = useState({});

    const rentalsStats = dashboard?.monthlyStats ?? [];
    const servicesStats = dashboard?.services ?? [];
    const topStats = dashboard?.topTools ?? [];
    const rentalsInPeriod = dashboard?.rentals ?? [];
    const globalCA = dashboard?.globalCA ?? 0;
    const ongoingCA = dashboard?.ongoingCA ?? 0;
    const ongoingCANextMonth = dashboard?.ongoingCANextMonth ?? 0;

    const periodRevenue = dashboard?.periodRevenue ?? 0;
    const periodFees = dashboard?.periodFees ?? 0;
    const previousPeriodRevenue = dashboard?.previousPeriodRevenue ?? 0;
    const previousPeriodFees = dashboard?.previousPeriodFees ?? 0;
    const previousPeriodRentalsCount = dashboard?.previousPeriodRentalsCount ?? 0;
    const periodRentalsCount = dashboard?.periodRentalsCount ?? 0;
    const periodBenefits = periodRevenue - periodFees;
    const periodMargin = periodRevenue > 0 ? (periodBenefits / periodRevenue) * 100 : 0;
    const averageTicket = periodRentalsCount > 0 ? periodRevenue / periodRentalsCount : 0;
    const previousPeriodBenefits = previousPeriodRevenue - previousPeriodFees;
    const periodRevenueDelta = periodRevenue - previousPeriodRevenue;
    const periodRevenueDeltaPercent = previousPeriodRevenue > 0 ? (periodRevenueDelta / previousPeriodRevenue) * 100 : 0;
    const periodBenefitsDelta = periodBenefits - previousPeriodBenefits;
    const periodBenefitsDeltaPercent = previousPeriodBenefits > 0 ? (periodBenefitsDelta / previousPeriodBenefits) * 100 : 0;
    const periodRentalsDelta = periodRentalsCount - previousPeriodRentalsCount;

    const getMonthKey = (value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        return date.getFullYear() * 12 + date.getMonth();
    };

    const currentMonthKey = currentMonthStart.getFullYear() * 12 + currentMonthStart.getMonth();
    const nextMonthKey = currentMonthKey + 1;
    const nextMonthDate = new Date(currentMonthStart);
    nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
    const selectedPeriodLabel = `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`;
    const previousPeriodEndDate = new Date(startDate);
    previousPeriodEndDate.setDate(previousPeriodEndDate.getDate() - 1);
    const previousPeriodStartDate = new Date(previousPeriodEndDate);
    previousPeriodStartDate.setDate(previousPeriodStartDate.getDate() - Math.max(0, moment(endDate).diff(moment(startDate), "days")));
    const previousPeriodLabel = `${moment(previousPeriodStartDate).format("DD/MM/YYYY")} - ${moment(previousPeriodEndDate).format("DD/MM/YYYY")}`;
    const currentMonthLabel = `${moment(currentMonthStart).format("DD/MM/YYYY")} - ${moment(new Date()).format("DD/MM/YYYY")}`;

    const rentalsStatsWithProjection = [...rentalsStats];
    const hasNextMonth = rentalsStatsWithProjection.some((stat) => getMonthKey(stat.month) === nextMonthKey);
    if (!hasNextMonth) {
        rentalsStatsWithProjection.push({
            month: nextMonthDate.toISOString(),
            revenue: 0,
            fees: 0,
        });
    }

    rentalsStatsWithProjection.sort((a, b) => {
        const aKey = getMonthKey(a.month) ?? Number.MIN_SAFE_INTEGER;
        const bKey = getMonthKey(b.month) ?? Number.MIN_SAFE_INTEGER;
        return aKey - bKey;
    });

    const chartRentalsLabels = rentalsStatsWithProjection.map((stat) =>
        new Date(stat.month).toLocaleDateString("fr", {
            year: "2-digit",
            month: "2-digit",
        })
    );
    const chartRentalsValues = rentalsStatsWithProjection.map((stat) => stat.revenue);
    const fees = rentalsStatsWithProjection.map((stat) => stat.fees);
    const benefits = rentalsStatsWithProjection.map((stat) => {
        return isAfterCurrentMonth(stat.month) ? null : stat.revenue - stat.fees;
    });

    const trailingRevenue = chartRentalsValues.reduce((acc, value) => acc + value, 0);
    const trailingBenefits = benefits.reduce((acc, value) => acc + value, 0);
    const trailingMargin = trailingRevenue > 0 ? (trailingBenefits / trailingRevenue) * 100 : 0;
    const loadedCurrentMonthChargesTotal = chargeGroups.reduce((acc, group) => acc + group.amount, 0);
    const currentMonthChargesTotal = chargesLoaded ? loadedCurrentMonthChargesTotal : dashboard?.currentMonthFees ?? 0;

    const loadCurrentMonthCharges = async () => {
        if (chargesLoaded || chargesLoading) {
            return;
        }

        setChargesLoading(true);
        setChargesError(null);
        try {
            const groups = await CurrentMonthCharges();
            setChargeGroups(Array.isArray(groups) ? groups : []);
            setExpandedChargeGroups({});
            setChargesLoaded(true);
        } catch (error) {
            setChargesError(error?.message || "Impossible de charger les charges du mois.");
        } finally {
            setChargesLoading(false);
        }
    };

    const getChargeGroupKey = (group) => group.rentalPublicId ?? "no-rental";

    const toggleChargeGroup = (group) => {
        const groupKey = getChargeGroupKey(group);
        setExpandedChargeGroups((current) => ({
            ...current,
            [groupKey]: !current[groupKey]
        }));
    };

    const toggleCharges = async () => {
        const nextExpanded = !chargesExpanded;
        setChargesExpanded(nextExpanded);
        if (nextExpanded) {
            await loadCurrentMonthCharges();
        }
    };

    const servicesBreakdown = servicesStats
        .map((service) => ({
            name: service.serviceName,
            revenue: service.revenue,
            rentalsCount: service.rentalsCount,
        }))
        .sort((a, b) => b.revenue - a.revenue);

    const chartServicesLabels = servicesBreakdown.map((service) => service.name);
    const chartServicesValues = servicesBreakdown.map((service) => service.revenue);
    const totalServicesRevenue = chartServicesValues.reduce((acc, value) => acc + value, 0);

    const topServices = servicesBreakdown.slice(0, 4);

    const bestService = servicesBreakdown[0];
    const bestServiceShare =
        bestService && totalServicesRevenue > 0 ? (bestService.revenue / totalServicesRevenue) * 100 : 0;

    const topTools = topStats
        .map((tool) => ({
            publicId: tool.publicId,
            name: tool.name,
            rentalsCount: tool.rentalsCount,
            revenue: tool.revenue,
            // Matériel partagé entre plusieurs annonces : ligne cumulée, annonces listées sous le nom.
            equipmentToolNames: tool.equipmentToolNames ?? null,
        }))
        .sort((a, b) => b.revenue - a.revenue);

    const chartTopLabels = topTools.map((tool) => tool.name);
    const chartTopValues = topTools.map((tool) => tool.revenue);
    const totalTopToolsRevenue = chartTopValues.reduce((acc, value) => acc + value, 0);

    const topTool = topTools[0];

    const currentMonthIndex = rentalsStatsWithProjection.findIndex((stat) => getMonthKey(stat.month) === currentMonthKey);
    const lastPastOrCurrentMonthIndex = rentalsStatsWithProjection.findLastIndex((stat) => !isAfterCurrentMonth(stat.month));
    const currentMonthRevenue =
        currentMonthIndex >= 0 ? rentalsStatsWithProjection[currentMonthIndex].revenue + ongoingCA : 0;
    const currentMonthBaseRevenue =
        currentMonthIndex >= 0 ? rentalsStatsWithProjection[currentMonthIndex].revenue : 0;
    const currentMonthBenefits = currentMonthRevenue - currentMonthChargesTotal;
    const currentMonthMargin = currentMonthRevenue > 0 ? (currentMonthBenefits / currentMonthRevenue) * 100 : 0;
    const previousMonthRevenue =
        lastPastOrCurrentMonthIndex > 0 ? rentalsStatsWithProjection[lastPastOrCurrentMonthIndex - 1].revenue : 0;
    const revenueDelta = currentMonthRevenue - previousMonthRevenue;
    const revenueDeltaPercent = previousMonthRevenue > 0 ? (revenueDelta / previousMonthRevenue) * 100 : 0;

    const ongoingPayment = rentalsStatsWithProjection.map(() => null);
    if (currentMonthIndex >= 0 && ongoingCA > 0) {
        const currentRevenueBase = rentalsStatsWithProjection[currentMonthIndex].revenue;
        ongoingPayment[currentMonthIndex] = [currentRevenueBase, currentRevenueBase + ongoingCA];
    }

    const nextMonthIndex = rentalsStatsWithProjection.findIndex((stat) => getMonthKey(stat.month) === nextMonthKey);
    if (nextMonthIndex >= 0 && ongoingCANextMonth > 0) {
        const nextRevenueBase = rentalsStatsWithProjection[nextMonthIndex].revenue;
        ongoingPayment[nextMonthIndex] = [nextRevenueBase, nextRevenueBase + ongoingCANextMonth];
    }

    const [chartDataRentals, setChartDataRentals] = useState({});
    const [chartOptionsRentals, setChartOptionsRentals] = useState({});
    const [chartDataServices, setChartDataServices] = useState({});
    const [chartOptionsServices, setChartOptionsServices] = useState({});
    const [chartDataTop, setChartDataTop] = useState({});
    const [chartOptionsTop, setChartOptionsTop] = useState({});

    const rentTooltips = useMemo(
        () => ({
            callbacks: {
                label: (context) => {
                    const value = context.parsed.y ?? 0;
                    return `${context.dataset.label}: ${formatCurrency(value)}`;
                },
            },
        }),
        []
    );

    useEffect(() => {
        const rentalsData = {
            labels: chartRentalsLabels,
            datasets: [
                {
                    label: "C.A.",
                    data: chartRentalsValues,
                    backgroundColor: RENTALS_COLORS,
                    borderColor: RENTALS_COLORS,
                    borderWidth: 1,
                    order: 2,
                },
                {
                    label: "Charges",
                    data: fees,
                    backgroundColor: "rgba(245, 39, 39, 0.5)",
                    borderColor: "rgba(245, 39, 39, 0.6)",
                    borderWidth: 1,
                    order: 3,
                },
                {
                    type: "line",
                    label: "Bénéfices",
                    borderColor: "rgba(43, 59, 80, 1)",
                    backgroundColor: "rgba(43, 59, 80, 0.15)",
                    borderWidth: 2,
                    fill: false,
                    tension: 0.35,
                    data: benefits,
                    order: 1,
                    yAxisID: "y",
                },
                {
                    label: "CA à venir",
                    data: ongoingPayment,
                    backgroundColor: "rgba(220, 220, 220, 0.7)",
                    borderColor: "rgba(220, 220, 220, 0.8)",
                    borderWidth: 1,
                    order: 4,
                },
            ],
        };
        const rentalsOptions = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: "index",
                intersect: false,
            },
            plugins: {
                legend: {
                    position: "bottom",
                },
                tooltip: rentTooltips,
            },
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: (value) => formatCurrency(Number(value)),
                    },
                },
            },
        };

        const servicesData = {
            labels: chartServicesLabels,
            datasets: [
                {
                    data: chartServicesValues,
                    backgroundColor: SERVICES_COLORS,
                    hoverBackgroundColor: SERVICES_COLORS,
                    borderColor: "rgba(255,255,255,0.6)",
                    borderWidth: 2,
                },
            ],
        };
        const servicesOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: "bottom",
                    labels: {
                        usePointStyle: true,
                    },
                },
                tooltip: {
                    callbacks: {
                        label: (context) => {
                            const revenue = context.parsed ?? 0;
                            const share = totalServicesRevenue > 0 ? (revenue / totalServicesRevenue) * 100 : 0;
                            return `${context.label}: ${formatCurrency(revenue)} (${formatPercent(share)})`;
                        },
                    },
                },
            },
        };

        const topData = {
            labels: chartTopLabels,
            datasets: [
                {
                    label: "C.A.",
                    data: chartTopValues,
                    backgroundColor: TOP_TOOLS_BACKGROUND_COLORS,
                    borderColor: TOP_TOOLS_BORDER_COLORS,
                    borderWidth: 1,
                    borderRadius: 6,
                },
            ],
        };
        const topOptions = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: rentTooltips,
            },
            scales: {
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: (value) => formatCurrency(Number(value)),
                    },
                },
            },
        };

        setChartDataRentals(rentalsData);
        setChartOptionsRentals(rentalsOptions);
        setChartDataServices(servicesData);
        setChartOptionsServices(servicesOptions);
        setChartDataTop(topData);
        setChartOptionsTop(topOptions);
    }, [rentalsStats, servicesStats, topStats, ongoingCA, ongoingCANextMonth, rentTooltips]);

    const periodCards = [
        {
            label: "CA période",
            value: formatCurrency(periodRevenue),
            hint: `${formatPercent(periodMargin)} de marge`,
        },
        {
            label: "Bénéfices période",
            value: formatCurrency(periodBenefits),
            hint: `${formatCurrency(periodFees)} de charges`,
        },
        {
            label: "Locations période",
            value: formatNumber(periodRentalsCount),
            hint: `${formatCurrency(averageTicket)} / location`,
        },
        {
            label: "CA en cours",
            value: formatCurrency(ongoingCA),
            hint: "À encaisser",
        },
    ];

    const trendCards = [
        {
            label: "CA 12 mois",
            value: formatCurrency(trailingRevenue),
            hint: `${formatCurrency(trailingBenefits)} net`,
        },
        {
            label: "CA global",
            value: formatCurrency(globalCA),
            hint: "Depuis l'origine",
        },
        {
            label: "Ce mois",
            value: formatCurrency(currentMonthRevenue),
            hint:
                previousMonthRevenue > 0
                    ? `${revenueDelta >= 0 ? "+" : ""}${formatCurrency(revenueDelta)} (${formatPercent(revenueDeltaPercent)})`
                    : "Pas de comparaison",
            tone: revenueDelta >= 0 ? "positive" : "negative",
        },
    ];

    void periodCards;
    void trendCards;

    const renderDelta = (value, percent = null, type = "currency") => {
        const sign = value >= 0 ? "+" : "";
        const formattedValue = type === "number" ? `${sign}${formatNumber(value)}` : `${sign}${formatCurrency(value)}`;
        return percent === null ? formattedValue : `${formattedValue} (${formatPercent(percent)})`;
    };

    const renderMetric = (label, value, hint = null, tone = null) => (
        <div className={`stats-summary-metric ${tone ? `stats-summary-metric-${tone}` : ""}`}>
            <span>{label}</span>
            <strong>{value}</strong>
            {hint && <small>{hint}</small>}
        </div>
    );

    return (
        <div className="stats-page">
            <div id="thumbnail" />
            <div id="mainContent" className="stats-page-content">
                <header className="stats-page-header">
                    <div>
                        <h1>Statistiques locations & CA</h1>
                        <p>
                            Vue synthétique du {moment(startDate).format("DD/MM/YYYY")} au {" "}
                            {moment(endDate).format("DD/MM/YYYY")}.
                        </p>
                    </div>
                    <div className="stats-page-header-meta">
                        <div className="stats-page-meta-item">
                            <span>Période</span>
                            <strong>
                                {moment(startDate).format("DD MMM")} → {moment(endDate).format("DD MMM YYYY")}
                            </strong>
                        </div>
                        <div className="stats-page-meta-item">
                            <span>Locations</span>
                            <strong>{formatNumber(periodRentalsCount)}</strong>
                        </div>
                        <div className="stats-page-meta-item">
                            <span>CA période</span>
                            <strong>{formatCurrency(periodRevenue)}</strong>
                        </div>
                    </div>
                </header>

                <section className="stats-filter card">
                    <Form method="post" className="stats-filter-form">
                        <div className="stats-filter-fields">
                            <Calendar
                                name="dates"
                                dateFormat="dd/mm/yy"
                                value={dates}
                                onChange={(e) => setDates(e.value)}
                                selectionMode="range"
                                readOnlyInput
                                hideOnRangeSelection
                            />
                            <Button label="Mettre à jour" type="submit" />
                        </div>
                        <p className="stats-filter-help">
                            Ajustez la période pour analyser la saisonnalité, la performance par service et les tops outils.
                        </p>
                    </Form>
                </section>

                <section className="stats-summary-layout">
                    <article className="stats-summary-card card stats-summary-card-primary">
                        <div className="stats-summary-card-header">
                            <span>Période sélectionnée</span>
                            <strong>{selectedPeriodLabel}</strong>
                        </div>
                        <div className="stats-summary-metrics">
                            {renderMetric("Chiffre d'affaires", formatCurrency(periodRevenue), `${formatPercent(periodMargin)} de marge`)}
                            {renderMetric("Résultat", formatCurrency(periodBenefits), `${formatCurrency(periodFees)} de charges`)}
                            {renderMetric("Locations", formatNumber(periodRentalsCount), `${formatCurrency(averageTicket)} / location`)}
                        </div>
                    </article>

                    <article className="stats-summary-card card">
                        <div className="stats-summary-card-header">
                            <span>Même durée précédente</span>
                            <strong>{previousPeriodLabel}</strong>
                        </div>
                        <div className="stats-summary-metrics">
                            {renderMetric("CA précédent", formatCurrency(previousPeriodRevenue), renderDelta(periodRevenueDelta, periodRevenueDeltaPercent), periodRevenueDelta >= 0 ? "positive" : "negative")}
                            {renderMetric("Résultat précédent", formatCurrency(previousPeriodBenefits), renderDelta(periodBenefitsDelta, periodBenefitsDeltaPercent), periodBenefitsDelta >= 0 ? "positive" : "negative")}
                            {renderMetric("Locations précédentes", formatNumber(previousPeriodRentalsCount), renderDelta(periodRentalsDelta, null, "number"), periodRentalsDelta >= 0 ? "positive" : "negative")}
                        </div>
                    </article>

                    <article className="stats-summary-card card stats-summary-card-current">
                        <div className="stats-summary-card-header">
                            <span>Mois en cours</span>
                            <strong>{currentMonthLabel}</strong>
                        </div>
                        <div className="stats-summary-metrics">
                            {renderMetric("CA du mois", formatCurrency(currentMonthRevenue), `${formatCurrency(currentMonthBaseRevenue)} terminé + ${formatCurrency(ongoingCA)} à encaisser`)}
                            {renderMetric("Charges", formatCurrency(currentMonthChargesTotal), "Frais, dépenses et marchandises")}
                            {renderMetric("Résultat estimé", formatCurrency(currentMonthBenefits), `${formatPercent(currentMonthMargin)} de marge`, currentMonthBenefits >= 0 ? "positive" : "negative")}
                        </div>
                    </article>
                </section>

                <section className="stats-section card">
                    <div className="stats-section-header">
                        <div>
                            <h2>Évolution mensuelle</h2>
                            <p>Suivi du CA, des charges et du bénéfice sur 12 mois glissants.</p>
                        </div>
                        <div className="stats-section-side">
                            <span className="stats-side-label">Marge 12 mois</span>
                            <strong className="stats-side-value">{formatPercent(trailingMargin)}</strong>
                            <span className="stats-side-subvalue">{formatCurrency(trailingBenefits)}</span>
                        </div>
                    </div>
                    {rentalsStats.length === 0 && <i>Aucune statistique pour le moment.</i>}
                    {rentalsStats.length > 0 && (
                        <div className="stats-chart" role="img" aria-label="Graphique de CA mensuel">
                            <Chart type="bar" data={chartDataRentals} options={chartOptionsRentals} />
                        </div>
                    )}
                </section>

                <section className="stats-section card">
                    <div className="stats-section-header">
                        <div>
                            <h2>Charges du mois en cours</h2>
                            <p>Détail des frais, dépenses et marchandises consommées sur le mois courant.</p>
                        </div>
                        <div className="stats-section-actions">
                            <span className="stats-section-total">
                                <span>Total</span>
                                <strong>{formatCurrency(currentMonthChargesTotal)}</strong>
                            </span>
                            <Button
                                type="button"
                                className="stats-section-toggle"
                                icon={chargesExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                                label={chargesExpanded ? "Masquer" : "Afficher"}
                                text
                                aria-label={chargesExpanded ? "Replier les charges" : "Déplier les charges"}
                                aria-expanded={chargesExpanded}
                                loading={chargesLoading}
                                onClick={toggleCharges}
                            />
                        </div>
                    </div>
                    {chargesExpanded && (
                        <div className="stats-charges-panel">
                            {chargesError && <p className="stats-empty-state">{chargesError}</p>}
                            {!chargesError && chargesLoading && <p className="stats-empty-state">Chargement des charges...</p>}
                            {!chargesError && !chargesLoading && chargeGroups.length ? (
                                <>
                                    {chargeGroups.map((group) => {
                                        const groupKey = getChargeGroupKey(group);
                                        const isGroupExpanded = !!expandedChargeGroups[groupKey];

                                        return (
                                            <div key={groupKey} className="stats-charge-group">
                                                <div className="stats-charge-group-header">
                                                    <Button
                                                        type="button"
                                                        className="stats-charge-group-toggle"
                                                        icon={isGroupExpanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}
                                                        text
                                                        aria-label={isGroupExpanded ? "Replier la location" : "Déplier la location"}
                                                        aria-expanded={isGroupExpanded}
                                                        onClick={() => toggleChargeGroup(group)}
                                                    />
                                                    <div className="stats-charge-group-main">
                                                        {group.rentalPublicId ? (
                                                            <a href={`/${group.rentalPublicId}`} className="stats-charge-group-title">
                                                                {group.label}
                                                            </a>
                                                        ) : (
                                                            <strong className="stats-charge-group-title">{group.label}</strong>
                                                        )}
                                                        <span>{group.charges.length} ligne{group.charges.length > 1 ? "s" : ""}</span>
                                                    </div>
                                                    <strong className="stats-charge-group-amount">{formatCurrency(group.amount)}</strong>
                                                </div>
                                                {isGroupExpanded && (
                                                    <div className="stats-tools-table stats-charge-lines">
                                                        <div className="stats-tools-table-header stats-charges-header">
                                                            <span>Date</span>
                                                            <span>Type</span>
                                                            <span>Libellé</span>
                                                            <span>Montant</span>
                                                        </div>
                                                        {group.charges.map((charge, index) => (
                                                            <div key={`${groupKey}-${charge.category}-${charge.label}-${index}`} className="stats-tools-row stats-charges-row">
                                                                <span>{new Date(charge.date).toLocaleDateString("fr-FR")}</span>
                                                                <span>{charge.category}</span>
                                                                <span>{charge.label}</span>
                                                                <strong>{formatCurrency(charge.amount)}</strong>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </>
                            ) : (
                                !chargesError && !chargesLoading && <p className="stats-empty-state">Aucune charge sur le mois courant.</p>
                            )}
                        </div>
                    )}
                </section>

                <section className="stats-section stats-section-split">
                    <div className="card stats-split-main">
                        <div className="stats-section-header">
                            <div>
                                <h2>Répartition par service (CA)</h2>
                                <p>Distribution du chiffre d'affaires par service sur la période sélectionnée.</p>
                            </div>
                            {bestService && (
                                <div className="stats-section-side">
                                    <span className="stats-side-label">Service #1</span>
                                    <strong className="stats-side-value">{bestService.name}</strong>
                                    <span className="stats-side-subvalue">
                                        {formatCurrency(bestService.revenue)} · {formatNumber(bestService.rentalsCount)} loc.
                                    </span>
                                    <span className="stats-side-footnote">{formatPercent(bestServiceShare)} du CA</span>
                                </div>
                            )}
                        </div>
                        {servicesBreakdown.length === 0 && <i>Aucune statistique pour cette période.</i>}
                        {servicesBreakdown.length > 0 && (
                            <div className="stats-chart stats-chart-pie">
                                <Chart type="doughnut" data={chartDataServices} options={chartOptionsServices} />
                            </div>
                        )}
                    </div>

                    <aside className="card stats-split-side">
                        <h3>Services moteurs</h3>
                        {topServices.length === 0 && <i>Aucune donnée.</i>}
                        {topServices.length > 0 && (
                            <ul className="stats-ranking">
                                {topServices.map((service) => {
                                    const share = totalServicesRevenue > 0 ? (service.revenue / totalServicesRevenue) * 100 : 0;
                                    return (
                                        <li key={service.name} className="stats-ranking-item">
                                            <div className="stats-ranking-main">
                                                <strong>{service.name}</strong>
                                                <span>
                                                    {formatCurrency(service.revenue)} · {formatNumber(service.rentalsCount)} locations
                                                </span>
                                            </div>
                                            <div className="stats-ranking-bar">
                                                <div
                                                    className="stats-ranking-bar-fill"
                                                    style={{ width: `${clampRatio(share)}%` }}
                                                />
                                            </div>
                                            <span className="stats-ranking-share">{formatPercent(share)}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </aside>
                </section>

                <DeliveryStats stats={deliveries} />

                <section className="stats-section card">
                    <div className="stats-section-header">
                        <div>
                            <h2>Locations sur la période</h2>
                            <p>Toutes les locations terminées entre les dates sélectionnées.</p>
                        </div>
                        <div className="stats-section-side">
                            <span className="stats-side-label">Total locations</span>
                            <strong className="stats-side-value">{formatNumber(rentalsInPeriod.length)}</strong>
                        </div>
                    </div>
                    <div className="stats-tools-table stats-rentals-table">
                        <div className="stats-tools-table-header stats-rentals-header">
                            <span>#</span>
                            <span>Fin</span>
                            <span>Client</span>
                            <span>Service / Outil</span>
                            <span>CA</span>
                        </div>
                        {rentalsInPeriod.length ? (
                            rentalsInPeriod.map((rental, index) => (
                                <a key={rental.publicId} href={`/${rental.publicId}`} className="stats-tools-row stats-rentals-row">
                                    <span>{index + 1}</span>
                                    <span>{new Date(rental.endDate).toLocaleDateString("fr-FR")}</span>
                                    <span>{rental.clientName}</span>
                                    <span>{rental.serviceName}{rental.toolName ? ` · ${rental.toolName}` : ""}</span>
                                    <strong>{formatCurrency(rental.revenue)}</strong>
                                </a>
                            ))
                        ) : (
                            <p>Aucune location sur cette période.</p>
                        )}
                    </div>
                </section>

                <section className="stats-section card">
                    <div className="stats-section-header">
                        <div>
                            <h2>Top outils (CA)</h2>
                            <p>Les équipements qui génèrent le plus de chiffre d'affaires sur la période.</p>
                        </div>
                        {topTool && (
                            <div className="stats-section-side">
                                <span className="stats-side-label">Outil #1</span>
                                <strong className="stats-side-value">{topTool.name}</strong>
                                <span className="stats-side-subvalue">
                                    {formatCurrency(topTool.revenue)} · {formatNumber(topTool.rentalsCount)} loc.
                                </span>
                            </div>
                        )}
                    </div>
                    {topTools.length === 0 && <i>Aucune statistique pour cette période.</i>}
                    {topTools.length > 0 && (
                        <div className="stats-tools-layout">
                            <div className="stats-chart stats-chart-bar">
                                <Chart type="bar" data={chartDataTop} options={chartOptionsTop} />
                            </div>
                            <div className="stats-tools-table">
                                <div className="stats-tools-table-header">
                                    <span>Outil</span>
                                    <span>CA</span>
                                    <span>Loc.</span>
                                    <span>Part CA</span>
                                </div>
                                {topTools.map((tool) => {
                                    const share = totalTopToolsRevenue > 0 ? (tool.revenue / totalTopToolsRevenue) * 100 : 0;
                                    return (
                                        <div key={tool.publicId ?? tool.name} className="stats-tools-row">
                                            <span>
                                                {tool.name}
                                                {tool.equipmentToolNames && (
                                                    <small className="stats-tools-equipment">{tool.equipmentToolNames.join(", ")}</small>
                                                )}
                                            </span>
                                            <strong>{formatCurrency(tool.revenue)}</strong>
                                            <span>{formatNumber(tool.rentalsCount)}</span>
                                            <span>{formatPercent(share)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </section>

                <section className="stats-section card">
                    <div className="stats-section-header">
                        <div>
                            <h2>Satisfaction des locataires</h2>
                            <p>Questionnaires envoyés à la clôture des locations terminées sur la période.</p>
                        </div>
                        {satisfaction && satisfaction.averageScore != null && (
                            <div className="stats-section-side">
                                <span className="stats-side-label">Note moyenne</span>
                                <strong className="stats-side-value">{formatScore(satisfaction.averageScore)}</strong>
                                <span className="stats-side-subvalue"><Stars score={satisfaction.averageScore} /></span>
                            </div>
                        )}
                    </div>
                    {!satisfaction && <p className="stats-empty-state">Statistiques de satisfaction indisponibles.</p>}
                    {satisfaction && satisfaction.surveysCount === 0 && (
                        <p className="stats-empty-state">
                            Aucun questionnaire sur cette période.
                            {satisfaction.enabled === false ? " L'envoi automatique est désactivé dans vos paramètres." : ""}
                        </p>
                    )}
                    {satisfaction && satisfaction.surveysCount > 0 && (
                        <div className="px-3 pb-3">
                            <KpiGrid stats={satisfaction} />
                            <div className="sat-results-grid">
                                <div className="sat-result-card">
                                    <h4>Répartition des notes globales</h4>
                                    <Distribution items={satisfaction.scoreDistribution} reverse />
                                </div>
                                <div className="sat-result-card">
                                    <h4>Par outil<small>Note moyenne et recommandation sur la période</small></h4>
                                    {satisfaction.tools.length === 0 && <p className="sat-empty">Aucun outil.</p>}
                                    <div className="sat-tool-list">
                                        {satisfaction.tools.map((tool) => (
                                            <a key={tool.toolPublicId} href={`/monoutil/${tool.toolPublicId}/satisfaction`} className="sat-tool-row">
                                                <span>
                                                    {tool.name}
                                                    <br />
                                                    <small>{tool.completedCount}/{tool.surveysCount} réponse{tool.surveysCount > 1 ? "s" : ""}{tool.recommendRate != null ? ` · ${formatRate(tool.recommendRate)} recommandent` : ""}</small>
                                                </span>
                                                <Stars score={tool.averageScore} />
                                                <strong>{formatScore(tool.averageScore)}</strong>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                                <div className="sat-result-card">
                                    <h4>Derniers commentaires</h4>
                                    <CommentsList comments={satisfaction.recentComments} />
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

export default Stats;
